import React from 'react';
import { Mail, Phone, MessageCircle, ArrowRight } from 'lucide-react';

interface ContactBlockProps {
  variant?: 'full' | 'compact';
  showHours?: boolean;
  introText?: string;
  extraContent?: React.ReactNode;
}

export const ContactBlock: React.FC<ContactBlockProps> = ({
  variant = 'full',
  showHours = false,
  introText,
  extraContent,
}) => {
  return (
    <div className="not-prose mt-6 mb-4">
      {introText && (
        <p className="text-gray-600 leading-relaxed mb-5">{introText}</p>
      )}

      <div className="rounded-2xl border border-teal-100 bg-gradient-to-b from-teal-50/60 to-white shadow-md hover:shadow-lg transition-shadow duration-300 overflow-hidden">
        {variant === 'full' && (
          <div className="px-6 py-4 border-b border-teal-100/60 bg-teal-50/40">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-lg bg-teal-600 flex items-center justify-center">
                <Mail className="w-4.5 h-4.5 text-white" />
              </div>
              <div>
                <h3 className="text-base font-semibold text-gray-900">Get in Touch</h3>
                <p className="text-xs text-gray-500">We are here to help</p>
              </div>
            </div>
          </div>
        )}

        <div className={variant === 'compact' ? 'p-4 sm:p-5' : 'p-1'}>
          <a
            href="mailto:Info@nursingcarebooking.com"
            className={`flex items-center gap-4 ${
              variant === 'full'
                ? 'px-5 py-4 border-b border-gray-100'
                : 'px-3 py-3 border-b border-gray-100'
            } group hover:bg-gray-50/80 transition-colors rounded-t-xl`}
          >
            <div className="w-10 h-10 rounded-full bg-teal-50 border border-teal-200 flex items-center justify-center flex-shrink-0 group-hover:bg-teal-100 transition-colors">
              <Mail className="w-4.5 h-4.5 text-teal-700" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-gray-900">Email</p>
              <p className="text-sm text-teal-700 truncate">Info@nursingcarebooking.com</p>
            </div>
            <ArrowRight className="w-4 h-4 text-gray-300 group-hover:text-teal-600 group-hover:translate-x-0.5 transition-all flex-shrink-0" />
          </a>

          <a
            href="tel:+447462365503"
            className={`flex items-center gap-4 ${
              variant === 'full'
                ? 'px-5 py-4 border-b border-gray-100'
                : 'px-3 py-3 border-b border-gray-100'
            } group hover:bg-gray-50/80 transition-colors`}
          >
            <div className="w-10 h-10 rounded-full bg-blue-50 border border-blue-200 flex items-center justify-center flex-shrink-0 group-hover:bg-blue-100 transition-colors">
              <Phone className="w-4.5 h-4.5 text-blue-700" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-gray-900">Telephone</p>
              <p className="text-sm text-blue-700">+44 7462 365503</p>
              {showHours && (
                <p className="text-xs text-gray-400 mt-0.5">Monday to Friday, 9am to 5pm</p>
              )}
            </div>
            <ArrowRight className="w-4 h-4 text-gray-300 group-hover:text-blue-600 group-hover:translate-x-0.5 transition-all flex-shrink-0" />
          </a>

          <a
            href="https://wa.me/447462365503"
            target="_blank"
            rel="noopener noreferrer"
            className={`flex items-center gap-4 ${
              variant === 'full'
                ? 'px-5 py-4'
                : 'px-3 py-3'
            } group bg-green-50/40 hover:bg-green-50 transition-colors rounded-b-xl`}
          >
            <div className="w-10 h-10 rounded-full bg-green-50 border border-green-200 flex items-center justify-center flex-shrink-0 group-hover:bg-green-100 transition-colors">
              <MessageCircle className="w-4.5 h-4.5 text-green-700" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-gray-900">WhatsApp</p>
              <p className="text-sm text-green-700">Message us on WhatsApp</p>
            </div>
            <ArrowRight className="w-4 h-4 text-gray-300 group-hover:text-green-600 group-hover:translate-x-0.5 transition-all flex-shrink-0" />
          </a>
        </div>
      </div>

      {extraContent && (
        <div className="mt-5 text-sm text-gray-600 leading-relaxed prose prose-sm prose-a:text-teal-700 prose-a:no-underline hover:prose-a:underline max-w-none">
          {extraContent}
        </div>
      )}
    </div>
  );
};
